import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtZMW, fmtDateTime, COMPANY } from "@/lib/helpers";
import { PageHeader, MetricCard, StatusBadge, Empty } from "@/components/primitives";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  LineChart, Line, CartesianGrid,
} from "recharts";
import { Package, Warning, ClockCountdown, Wrench, TrendUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import ExecutiveInsights from "@/components/ExecutiveInsights";
import AttentionStrip from "@/components/AttentionStrip";

export default function Dashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [movements, setMovements] = useState([]);
  const [tonnageSeries, setTonnageSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const [d, m, t] = await Promise.all([
        api.get("/dashboard"),
        api.get("/inventory/movements?limit=20"),
        api.get("/production/summary?days=365"),
      ]);
      setData(d.data);
      setMovements(m.data);
      // Roll daily series up into monthly totals
      const byMonth = new Map();
      (t.data?.daily_series || []).forEach((row) => {
        const m = String(row.day || row.date).slice(0, 7);
        byMonth.set(m, (byMonth.get(m) || 0) + Number(row.total || row.tonnes || 0));
      });
      const series = Array.from(byMonth.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, tonnes]) => ({ month, tonnes: Math.round(tonnes) }));
      setTonnageSeries(series);
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return <div className="phizo-label">Loading dashboard…</div>;
  }

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        overline="Control Room / Overview"
        title={COMPANY.name}
        subtitle="Live operations snapshot across inventory, approvals and service plans."
        testId="dashboard-header"
      />

      <AttentionStrip />

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <MetricCard
          testId="kpi-production"
          label="Today (Tonnes)"
          value={`${(data.production_today_tonnes || 0).toLocaleString()} t`}
          delta={`${data.production_progress_pct || 0}% of ${(data.production_target_tonnes || 4000).toLocaleString()}t`}
          deltaLabel="Daily Target"
          accent={data.production_progress_pct >= 100 ? "success" : data.production_progress_pct >= 75 ? "primary" : "warning"}
          onClick={() => nav("/production")}
        />
        <MetricCard
          testId="kpi-total-value"
          label="Inventory Value"
          value={fmtZMW(data.total_value)}
          delta={`${data.total_items} SKUs`}
          deltaLabel="Tracked"
          accent="primary"
          onClick={() => nav("/inventory")}
        />
        <MetricCard
          testId="kpi-low-stock"
          label="Low Stock"
          value={data.low_stock_count}
          delta={`${data.low_stock_count > 0 ? "Attention required" : "All healthy"}`}
          deltaLabel="Status"
          accent={data.low_stock_count > 0 ? "danger" : "success"}
          onClick={() => nav("/inventory?low=1")}
        />
        <MetricCard
          testId="kpi-pending-approvals"
          label="Pending Approvals"
          value={data.pending_pos + data.pending_requests}
          delta={`${data.pending_pos} POs · ${data.pending_requests} Requests`}
          deltaLabel="Queue"
          accent="warning"
          onClick={() => nav("/purchase-orders?status=pending")}
        />
      </div>

      <ExecutiveInsights />

      {/* Monthly Tonnage Trend */}
      {tonnageSeries.length > 1 && (
        <div className="phizo-card p-5 mb-6" data-testid="tonnage-trend">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="phizo-label">Production Trend</div>
              <div className="font-heading text-xl font-bold mt-1 flex items-center gap-2">
                <TrendUp weight="bold" size={18} className="text-[#00A142]" />
                Monthly Tonnage
              </div>
            </div>
            <div className="text-right">
              <div className="phizo-label text-[10px]">Total (last 12m)</div>
              <div className="mono font-bold text-lg">
                {tonnageSeries.reduce((s, r) => s + r.tonnes, 0).toLocaleString()} t
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={tonnageSeries}>
              <CartesianGrid stroke="#e4e4e7" vertical={false} />
              <XAxis dataKey="month" stroke="#a1a1aa" fontSize={11} />
              <YAxis stroke="#a1a1aa" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ border: "1px solid #e4e4e7", borderRadius: 2, fontSize: 12 }}
                formatter={(v) => `${Number(v).toLocaleString()} t`}
              />
              <Bar dataKey="tonnes" fill="#002FA7" radius={0} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
        <div className="phizo-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="phizo-label">Stock Movement (14 days)</div>
              <div className="font-heading text-xl font-bold mt-1">Throughput</div>
            </div>
            <TrendUp size={22} className="text-[#002FA7]" weight="bold" />
          </div>
          {data.movement_series.length === 0 ? (
            <div className="h-52 flex items-center justify-center text-sm text-[#a1a1aa]">
              No movements logged yet. Adjust stock to start tracking.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <LineChart data={data.movement_series}>
                <CartesianGrid stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="day" stroke="#a1a1aa" fontSize={11} />
                <YAxis stroke="#a1a1aa" fontSize={11} />
                <Tooltip
                  contentStyle={{ border: "1px solid #e4e4e7", borderRadius: 2, fontSize: 12 }}
                />
                <Line
                  type="linear" dataKey="moves" stroke="#002FA7" strokeWidth={2}
                  dot={{ fill: "#002FA7", r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="phizo-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="phizo-label">Value by Category</div>
              <div className="font-heading text-xl font-bold mt-1">Capital tied</div>
            </div>
            <Package size={22} className="text-[#09090b]" weight="bold" />
          </div>
          {data.category_breakdown.length === 0 ? (
            <div className="h-52 flex items-center justify-center text-sm text-[#a1a1aa]">
              Add inventory to see breakdown.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={data.category_breakdown.slice(0, 6)} layout="vertical">
                <CartesianGrid stroke="#e4e4e7" horizontal={false} />
                <XAxis type="number" stroke="#a1a1aa" fontSize={11} />
                <YAxis dataKey="category" type="category" stroke="#a1a1aa" fontSize={11} width={90} />
                <Tooltip
                  contentStyle={{ border: "1px solid #e4e4e7", borderRadius: 2, fontSize: 12 }}
                  formatter={(v) => fmtZMW(v)}
                />
                <Bar dataKey="value" fill="#002FA7" radius={0} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Lower grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Low stock list */}
        <div className="phizo-card">
          <div className="p-5 border-b border-[#e4e4e7] flex items-center justify-between">
            <div>
              <div className="phizo-label">Action Required</div>
              <div className="font-heading text-xl font-bold mt-1 flex items-center gap-2">
                <Warning weight="fill" size={18} className="text-[#FF2A2A]" /> Low Stock
              </div>
            </div>
          </div>
          {data.low_stock_items.length === 0 ? (
            <div className="p-5"><Empty label="All stock levels healthy." /></div>
          ) : (
            <table className="phizo-table">
              <thead>
                <tr><th>SKU</th><th>Name</th><th className="text-right">Qty</th><th className="text-right">Reorder</th></tr>
              </thead>
              <tbody>
                {data.low_stock_items.map((i) => (
                  <tr key={i.id}>
                    <td className="mono text-xs">{i.sku}</td>
                    <td className="font-medium">{i.name}</td>
                    <td className="text-right mono text-[#FF2A2A] font-bold">{i.quantity}</td>
                    <td className="text-right mono text-[#52525b]">{i.reorder_level}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Movement log */}
        <div className="phizo-card">
          <div className="p-5 border-b border-[#e4e4e7]">
            <div className="phizo-label">Activity Log</div>
            <div className="font-heading text-xl font-bold mt-1 flex items-center gap-2">
              <ClockCountdown size={18} className="text-[#09090b]" weight="bold" /> Recent Movements
            </div>
          </div>
          {movements.length === 0 ? (
            <div className="p-5"><Empty label="No stock activity yet." /></div>
          ) : (
            <div className="max-h-[360px] overflow-y-auto">
              <table className="phizo-table">
                <thead>
                  <tr><th>When</th><th>SKU</th><th>Reason</th><th className="text-right">Delta</th></tr>
                </thead>
                <tbody>
                  {movements.map((m) => (
                    <tr key={m.id}>
                      <td className="text-xs text-[#52525b]">{fmtDateTime(m.at)}</td>
                      <td className="mono text-xs">{m.sku}</td>
                      <td className="text-xs">{m.reason}</td>
                      <td className={`text-right mono font-bold ${m.delta >= 0 ? "text-[#00A142]" : "text-[#FF2A2A]"}`}>
                        {m.delta >= 0 ? "+" : ""}{m.delta}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
